import { CalendarAttendee, CalendarEvent, CalendarLocation } from '@cue/calendars';
import { AssistResourceBM, AssistResourceFileBM, EventStatusResponse, StatusesByDay } from '@cue/api';
import { getResourceTimeZone, resourceAttendeeAccepted, ResourceAttendeeStatus } from '../../utils';
import {
  add,
  differenceInMinutes,
  endOfDay,
  getHours,
  getMinutes,
  getSeconds,
  isSameDay,
  isWithinInterval,
  startOfDay,
} from 'date-fns';
import { toZonedTime } from 'date-fns-tz';

export type TemporalResourceAttendee = {
  attendee: CalendarAttendee;
  resource: AssistResourceBM;
  location?: CalendarLocation;
};

export type ResourceConfirmationMetadata = {
  accepted: boolean;
  canceled: boolean;
  finished: boolean;
  requiresConfirmationByAssist: boolean;
  requiresConfirmationOnPanel: boolean;
  requiresConfirmationBySensor: boolean;
  confirmed: boolean;
  confirmByAssistAvailable: boolean;
  confirmBySensorAvailable: boolean;
  confirmByPanelAvailable: boolean;
  lowerBorderTime: Date | null;
  higherBorderTime: Date | null;
};

export function getResourceConfirmationMetadata(
  response: EventStatusResponse,
  calendarEvent: CalendarEvent,
  rA: TemporalResourceAttendee
): ResourceConfirmationMetadata {
  let lowerBorderTime: Date | null = null;
  let higherBorderTime: Date | null = null;

  const requiresConfirmationByAssist =
    rA.resource.confirmationSettings.requiresConfirmation &&
    rA.resource.confirmationSettings.actionForUnconfirmedEvent !== 'none';

  const requiresConfirmationBySensor =
    rA.resource.confirmationSettings.requiresConfirmationBySensor &&
    rA.resource.confirmationSettings.actionForUnconfirmedEvent !== 'none';

  const requiresConfirmationOnPanel =
    rA.resource.confirmationSettings.requiresConfirmationOnPanel &&
    rA.resource.confirmationSettings.actionForUnconfirmedEvent !== 'none';

  const isMultiDay = !isSameDay(calendarEvent.start.utcDateTime, calendarEvent.end.utcDateTime);
  const duration = differenceInMinutes(calendarEvent.end.utcDateTime, calendarEvent.start.utcDateTime);

  const beforeMinutes = rA.resource.confirmationSettings.resourceMinutesForConfirmationBeforeStartTime
    ? rA.resource.confirmationSettings.resourceMinutesForConfirmationBeforeStartTime
    : 15;

  const cancelMinutes = rA.resource.confirmationSettings.resourceUnconfirmedEventCancelEventTime
    ? rA.resource.confirmationSettings.resourceUnconfirmedEventCancelEventTime
    : 15;

  const ianaTimeZone = getResourceTimeZone(rA.resource.workingHours);
  const nowInResourceTimeZone = toZonedTime(new Date(), ianaTimeZone);

  const startTimeInResourceTimeZone = toZonedTime(calendarEvent.start.utcDateTime, ianaTimeZone);
  const endTimeInResourceTimeZone = toZonedTime(calendarEvent.end.utcDateTime, ianaTimeZone);

  const isMidnight =
    getHours(startTimeInResourceTimeZone) === 0 &&
    getMinutes(startTimeInResourceTimeZone) === 0 &&
    getSeconds(startTimeInResourceTimeZone) === 0;

  if (isMultiDay) {
    const isMultiDayAndIsLessThamMinutesOverlay =
      isMultiDay &&
      rA.resource.confirmationSettings.multiDayEventConfirmationSettings.lessThanMinutes !== null &&
      duration <= rA.resource.confirmationSettings.multiDayEventConfirmationSettings.lessThanMinutes;
    const isMultiDayAndIsGreaterThanMinutesOverlay =
      isMultiDay &&
      rA.resource.confirmationSettings.multiDayEventConfirmationSettings.greaterThanMinutes !== null &&
      duration >= rA.resource.confirmationSettings.multiDayEventConfirmationSettings.greaterThanMinutes;

    const multiDayEventConfirmationId = isMultiDayAndIsLessThamMinutesOverlay
      ? rA.resource.confirmationSettings.multiDayEventConfirmationSettings
          .lessThanMinutesMultiDayEventConfirmationId === 2
      : isMultiDayAndIsGreaterThanMinutesOverlay
        ? rA.resource.confirmationSettings.multiDayEventConfirmationSettings
            .greaterThanMinutesMultiDayEventConfirmationId === 2
        : rA.resource.confirmationSettings.multiDayEventConfirmationSettings.multiDayEventConfirmationId;

    const isStartDayOnlyConfirmation = multiDayEventConfirmationId === 1;

    if (isStartDayOnlyConfirmation) {
      lowerBorderTime = add(startOfDay(calendarEvent.start.utcDateTime), {
        minutes: -beforeMinutes,
      });

      higherBorderTime = add(startOfDay(calendarEvent.start.utcDateTime), {
        minutes: cancelMinutes,
      });
    } else {
      // je potřeba spocitat "dnesek"
      const todayStart = startOfDay(nowInResourceTimeZone);
      const todayEnd = endOfDay(nowInResourceTimeZone);
      // todayStart > e.start.utcDateTime && todayEnd < e.end.utcDateTime
      const isAllDay =
        todayStart.getTime() === calendarEvent.start.utcDateTime.getTime() &&
        todayEnd.getTime() < calendarEvent.end.utcDateTime.getTime();
      const isLastDay = isMultiDay && isSameDay(todayEnd.getDay(), endTimeInResourceTimeZone);
      if (isAllDay || isLastDay) {
        // je to cely den - bere se ten den.
        const hours = rA.resource.confirmationSettings.resourceUnconfirmedEventCancelEventAllDayTime / 60;
        const minutes = rA.resource.confirmationSettings.resourceUnconfirmedEventCancelEventAllDayTime % 60;
        /// lower start of the day
        lowerBorderTime = todayStart;
        // lowe end is today start + hours + minutes
        higherBorderTime = add(lowerBorderTime, {
          hours: hours,
          minutes: minutes,
        });
      } else {
        lowerBorderTime = add(calendarEvent.start.utcDateTime, {
          minutes: -beforeMinutes,
        });

        higherBorderTime = add(calendarEvent.start.utcDateTime, {
          minutes: cancelMinutes,
        });
      }
    }
  } else {
    lowerBorderTime = add(calendarEvent.start.utcDateTime, {
      minutes: -beforeMinutes,
    });

    higherBorderTime = add(calendarEvent.start.utcDateTime, {
      minutes: cancelMinutes,
    });
  }

  const confirmFromMidnight = isMidnight ? startTimeInResourceTimeZone <= new Date() : false;

  const accepted = resourceAttendeeAccepted(rA);

  const finished = response ? response.finished : false;
  const canceled = response ? response.canceled : false;
  const confirmed = response ? response.confirmed : false;

  const confirmByAssistAvailable =
    // 1. event neni cancelled
    !calendarEvent.cancelled &&
    accepted !== false &&
    !confirmed &&
    requiresConfirmationByAssist &&
    ((isWithinInterval(new Date(), {
      start: lowerBorderTime,
      end: higherBorderTime,
    }) &&
      new Date() <= calendarEvent.end.utcDateTime) ||
      confirmFromMidnight);

  const confirmByPanelAvailable =
    // 1. event neni cancelled
    !calendarEvent.cancelled &&
    accepted !== false &&
    !confirmed &&
    requiresConfirmationOnPanel &&
    ((isWithinInterval(new Date(), {
      start: lowerBorderTime,
      end: higherBorderTime,
    }) &&
      new Date() <= calendarEvent.end.utcDateTime) ||
      confirmFromMidnight);

  const confirmBySensorAvailable =
    // 1. event neni cancelled
    !calendarEvent.cancelled &&
    accepted !== false &&
    !confirmed &&
    requiresConfirmationBySensor &&
    ((isWithinInterval(new Date(), {
      start: lowerBorderTime,
      end: higherBorderTime,
    }) &&
      new Date() <= calendarEvent.end.utcDateTime) ||
      confirmFromMidnight);

  return {
    accepted: accepted,
    canceled: canceled,
    finished: finished,
    confirmByAssistAvailable: confirmByAssistAvailable,
    confirmByPanelAvailable: confirmByPanelAvailable,
    confirmBySensorAvailable: confirmBySensorAvailable,
    confirmed: confirmed,
    lowerBorderTime: lowerBorderTime,
    higherBorderTime: higherBorderTime,
    requiresConfirmationByAssist: requiresConfirmationByAssist,
    requiresConfirmationBySensor: requiresConfirmationBySensor,
    requiresConfirmationOnPanel: requiresConfirmationOnPanel,
  } satisfies ResourceConfirmationMetadata;
}

// PODLE https://cue.atlassian.net/wiki/spaces/Solution/pages/edit-v2/1718190116
export function getResourceAttendeeStatusAndConfirmationMetadata(
  calendarEvent: CalendarEvent,
  temporalResourceAttendee: TemporalResourceAttendee,
  serverStatus: EventStatusResponse
): [ResourceAttendeeStatus, ResourceConfirmationMetadata] {
  const metadata = getResourceConfirmationMetadata(serverStatus, calendarEvent, temporalResourceAttendee);

  // Declined: Declined, odmítnut místností
  const accepted = resourceAttendeeAccepted(temporalResourceAttendee);
  if (accepted === false) {
    return [ResourceAttendeeStatus.Declined, metadata];
  }
  // Confirmed: potvrzen uživatelem přes assista nebo panel
  if (serverStatus.confirmed) {
    return [ResourceAttendeeStatus.Confirmed, metadata];
  }

  if (serverStatus.failed) {
    return [ResourceAttendeeStatus.ReleaseFailed, metadata];
  }

  // Released for non-confirmation: nepotvrzený uživatelem po uplynutí potvrzovací doby a uvolněná místnost backendem
  if (serverStatus.canceled) {
    return [ResourceAttendeeStatus.ReleasedForNonConfirmation, metadata];
  }

  if (
    metadata.requiresConfirmationOnPanel ||
    metadata.requiresConfirmationByAssist ||
    metadata.requiresConfirmationBySensor
  ) {
    // Need confirmation: před začátkem potvrzovací doby, ale zatím nepotvrzený
    if (metadata.lowerBorderTime && new Date() < metadata.lowerBorderTime) {
      return [ResourceAttendeeStatus.NeedConfirmation, metadata];
    }
    //Confirm now: uvnitř potvrzovací doby, ale zatím nepotvrzený
    if (metadata.confirmByAssistAvailable || metadata.confirmByPanelAvailable || metadata.confirmBySensorAvailable) {
      return [ResourceAttendeeStatus.ConfirmNow, metadata];
    }

    if (metadata.higherBorderTime && new Date() > metadata.higherBorderTime) {
      // Unconfirmed: nepotvrzený uživatelem po uplynutí potvrzovací doby, buď server ještě nestihl uvolnit místnost (může se překlopit do dalšího stavu během pár s/min) nebo je nastavené, že se má potvrzovat třeba kvůli analytikám, ale nemá se uvolňovat
      return [ResourceAttendeeStatus.Unconfirmed, metadata];
    }
  }

  // Accepted: Accepted a není ani jeden z předchozích stavů, např. resource vůbec nemá potvrzování zapnuté
  if (accepted) {
    return [ResourceAttendeeStatus.Accepted, metadata];
  }

  // Tentative: None, není to ani accepted ani declined nebo nevíme stav
  return [ResourceAttendeeStatus.Tentative, metadata];
}

export type ResourceAttendee = TemporalResourceAttendee & {
  confirmationMetadata: ResourceConfirmationMetadata;
  status: ResourceAttendeeStatus;
  orderItemsCount: number;
  files: AssistResourceFileBM[];
  reservationsEnabled: boolean;
  navigationEnabled: boolean;
  confirmedWhen: Date | null;
  confirmedBy: string | null;
  ianaTimeZone: string;
  statusesByDay: StatusesByDay[];
  // nahrazuje showConfirmButton
  confirmAvailable: boolean;
};
